import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Loader2, Check, X, AlertTriangle, RefreshCw, Send, FileText, ShoppingCart, Copy } from 'lucide-react';
import { API } from '../../config/apiBase';

// Painel admin do bot WhatsApp Cloud API.
// 4 sub-abas: Saúde, Credenciais, Logs, Pedidos
export default function WhatsAppManager({ token }) {
    const [subTab, setSubTab] = useState('health');
    const [health, setHealth] = useState(null);
    const [config, setConfig] = useState(null);
    const [logs, setLogs] = useState([]);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testTo, setTestTo] = useState('');
    const [testText, setTestText] = useState('');
    const [testResult, setTestResult] = useState(null);
    const [form, setForm] = useState({ waba_id: '', phone_number_id: '', access_token: '', display_name: 'Super Vitrine Ofertas', enabled: false });

    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [h, c, l, o] = await Promise.all([
                fetch(`${API}/admin/master/whatsapp-health`, { headers }).then(r => r.ok ? r.json() : null),
                fetch(`${API}/admin/master/whatsapp-config`, { headers }).then(r => r.ok ? r.json() : null),
                fetch(`${API}/admin/master/whatsapp-logs?limit=50`, { headers }).then(r => r.ok ? r.json() : null),
                fetch(`${API}/admin/master/whatsapp-orders?limit=50`, { headers }).then(r => r.ok ? r.json() : null),
            ]);
            if (h?.ok) setHealth(h.health || null);
            if (c?.ok) {
                setConfig(c.config || null);
                setForm(f => ({ ...f,
                    waba_id: c.config?.waba_id || '',
                    phone_number_id: c.config?.phone_number_id || '',
                    display_name: c.config?.display_name || 'Super Vitrine Ofertas',
                    enabled: !!c.config?.enabled,
                }));
            }
            if (l?.ok) setLogs(l.logs || []);
            if (o?.ok) setOrders(o.orders || []);
        } finally { setLoading(false); }
    }, [token]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const saveConfig = async () => {
        setSaving(true);
        try {
            const body = {
                waba_id: form.waba_id.trim(),
                phone_number_id: form.phone_number_id.trim(),
                display_name: form.display_name.trim(),
                enabled: form.enabled,
            };
            if (form.access_token.trim()) body.access_token = form.access_token.trim();
            const r = await fetch(`${API}/admin/master/whatsapp-config`, {
                method: 'PUT',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const d = await r.json();
            if (!d.ok) throw new Error(d.detail || 'falha ao salvar');
            setForm(f => ({ ...f, access_token: '' }));
            await loadAll();
        } catch (e) {
            alert('Erro ao salvar: ' + e.message);
        } finally { setSaving(false); }
    };

    const sendTest = async () => {
        if (!testTo.trim()) return;
        setTesting(true);
        setTestResult(null);
        try {
            const r = await fetch(`${API}/admin/master/whatsapp-test`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ to: testTo.trim(), text: testText.trim() || undefined }),
            });
            const d = await r.json();
            setTestResult(d);
        } catch (e) {
            setTestResult({ ok: false, error: e.message });
        } finally { setTesting(false); }
    };

    const copyText = (text) => {
        navigator.clipboard?.writeText(text).then(() => {/* ok */});
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--sv-gold-primary)' }} />
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="whatsapp-manager">
            {/* Sub-tabs */}
            <div className="flex items-center gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                {[
                    { key: 'health', label: 'Saúde', icon: MessageCircle },
                    { key: 'creds', label: 'Credenciais', icon: FileText },
                    { key: 'logs', label: 'Logs webhook', icon: RefreshCw },
                    { key: 'orders', label: 'Pedidos', icon: ShoppingCart },
                ].map(t => {
                    const Ic = t.icon;
                    const active = subTab === t.key;
                    return (
                        <button key={t.key} onClick={() => setSubTab(t.key)}
                            className="text-xs font-medium flex items-center gap-1.5 px-3 py-1.5 rounded-md whitespace-nowrap transition-colors"
                            style={{ background: active ? 'var(--sv-gold-bg, var(--surface-2))' : 'var(--surface)', color: active ? 'var(--sv-gold-primary)' : 'var(--muted)', border: `1px solid ${active ? 'var(--sv-gold-primary)' : 'var(--border)'}` }}>
                            <Ic size={12} />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {/* Saúde */}
            {subTab === 'health' && (
                <div className="space-y-3" data-testid="wa-health">
                    {/* Status geral */}
                    <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Status</h3>
                            <button onClick={loadAll} className="text-xs flex items-center gap-1" style={{ color: 'var(--muted)' }}>
                                <RefreshCw size={11} /> Atualizar
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <HealthRow label="Token Cloud API" ok={health?.access_token_present} />
                            <HealthRow label="WABA ID" ok={health?.waba_id_present} />
                            <HealthRow label="Phone Number ID" ok={health?.phone_number_id_present} />
                            <HealthRow label="Verify token (webhook)" ok={health?.verify_token_present} />
                            <HealthRow label="Habilitado" ok={health?.enabled} />
                            <HealthRow label="Graph API OK" ok={health?.graph_api_ok} />
                        </div>
                        {health?.error && (
                            <div className="mt-3 rounded-md p-2 text-[11px] flex items-start gap-2"
                                style={{ background: 'rgba(239,68,68,0.08)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.25)' }}>
                                <AlertTriangle size={12} />
                                <pre className="whitespace-pre-wrap flex-1">{String(health.error).slice(0, 400)}</pre>
                            </div>
                        )}
                    </div>

                    {/* Bot info */}
                    {health?.graph_api_ok && (
                        <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <h3 className="font-semibold text-sm mb-3" style={{ color: 'var(--text)' }}>Identidade do bot</h3>
                            <div className="space-y-1.5 text-xs" style={{ color: 'var(--text)' }}>
                                <div><span style={{ color: 'var(--muted)' }}>Display Phone:</span> {health.display_phone_number || '—'}</div>
                                <div><span style={{ color: 'var(--muted)' }}>Nome verificado:</span> {health.verified_name || '—'}</div>
                                <div><span style={{ color: 'var(--muted)' }}>Quality rating:</span> {health.quality_rating || '—'}</div>
                            </div>
                        </div>
                    )}

                    {/* Métricas */}
                    <div className="grid grid-cols-2 gap-3">
                        <Stat label="Usuários opt-in" value={health?.users_count ?? 0} icon={MessageCircle} />
                        <Stat label="Pedidos hoje" value={health?.orders_today ?? 0} icon={ShoppingCart} />
                    </div>

                    {/* Teste de envio */}
                    <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <h3 className="font-semibold text-sm mb-3" style={{ color: 'var(--text)' }}>Enviar mensagem de teste</h3>
                        <div className="space-y-2">
                            <input type="text" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="Número E.164 sem + (ex: 5565984484596)"
                                className="w-full px-3 py-2 rounded-md text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                            <input type="text" value={testText} onChange={e => setTestText(e.target.value)} placeholder="Mensagem (opcional)"
                                className="w-full px-3 py-2 rounded-md text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                            <button onClick={sendTest} disabled={testing || !testTo.trim() || !health?.enabled}
                                className="w-full text-xs font-semibold py-2 rounded-md flex items-center justify-center gap-1.5"
                                style={{ background: 'var(--sv-gold-btn, #FFB800)', color: '#0A0A0A', opacity: (testing || !testTo.trim() || !health?.enabled) ? 0.4 : 1 }}>
                                {testing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                                {testing ? 'Enviando...' : 'Enviar teste'}
                            </button>
                            {testResult && (
                                <pre className="text-[10px] p-2 rounded overflow-x-auto" style={{ background: testResult.ok ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', color: 'var(--text)' }}>
                                    {JSON.stringify(testResult, null, 2).slice(0, 600)}
                                </pre>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Credenciais */}
            {subTab === 'creds' && (
                <div className="space-y-3" data-testid="wa-creds">
                    <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <h3 className="font-semibold text-sm mb-2" style={{ color: 'var(--text)' }}>Credenciais Cloud API</h3>
                        <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>
                            Pegue em <a href={`https://developers.facebook.com/apps/`} target="_blank" rel="noreferrer" className="underline">developers.facebook.com</a> → seu app → WhatsApp → Configuração da API.
                        </p>
                        <div className="space-y-2">
                            <Field label="WABA ID (Identificação da conta WhatsApp Business)" value={form.waba_id} onChange={v => setForm(f => ({...f, waba_id: v}))} />
                            <Field label="Phone Number ID (Identificação do número de telefone)" value={form.phone_number_id} onChange={v => setForm(f => ({...f, phone_number_id: v}))} />
                            <Field label={`Access Token ${config?.has_access_token ? '(já salvo)' : ''}`}
                                placeholder={config?.has_access_token ? 'Deixe vazio pra não mudar' : 'Cole aqui (temporário ou permanente)'}
                                value={form.access_token} onChange={v => setForm(f => ({...f, access_token: v}))} secret />
                            <Field label="Display name" value={form.display_name} onChange={v => setForm(f => ({...f, display_name: v}))} />
                            <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: 'var(--text)' }}>
                                <input type="checkbox" checked={form.enabled} onChange={e => setForm(f => ({...f, enabled: e.target.checked}))} />
                                Bot habilitado (libera envio de mensagens)
                            </label>
                            <button onClick={saveConfig} disabled={saving}
                                className="text-xs font-semibold py-2 px-4 rounded-md flex items-center gap-1.5"
                                style={{ background: 'var(--sv-gold-btn, #FFB800)', color: '#0A0A0A', opacity: saving ? 0.5 : 1 }}>
                                {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                                Salvar
                            </button>
                        </div>
                    </div>

                    {/* Webhook setup info */}
                    <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <h3 className="font-semibold text-sm mb-2" style={{ color: 'var(--text)' }}>Configurar webhook na Meta</h3>
                        <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>
                            No painel do app → WhatsApp → Configuração → Webhooks → Editar. Use exatamente:
                        </p>
                        <KV label="URL de callback" value={config?.webhook_url || ''} onCopy={() => copyText(config?.webhook_url || '')} />
                        <KV label="Verify token" value={config?.verify_token || ''} onCopy={() => copyText(config?.verify_token || '')} hidden={!config?.verify_token} />
                        <p className="text-[10px] mt-3" style={{ color: 'var(--muted)' }}>
                            Depois de salvar a URL, marque a subscription <strong>messages</strong>. Salvo isso, mande "oi" pro número e o bot responde.
                        </p>
                    </div>
                </div>
            )}

            {/* Logs */}
            {subTab === 'logs' && (
                <div className="rounded-xl p-4 overflow-x-auto" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="wa-logs">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Últimos webhooks recebidos ({logs.length})</h3>
                        <button onClick={loadAll} className="text-xs flex items-center gap-1" style={{ color: 'var(--muted)' }}>
                            <RefreshCw size={11} /> Atualizar
                        </button>
                    </div>
                    {logs.length === 0 ? (
                        <p className="text-xs text-center py-6" style={{ color: 'var(--muted)' }}>Nenhum webhook recebido ainda.</p>
                    ) : (
                        <div className="space-y-2">
                            {logs.map((l, i) => (
                                <details key={i} className="rounded-md p-2" style={{ background: 'var(--surface-2)' }}>
                                    <summary className="text-[11px] cursor-pointer flex items-center gap-2" style={{ color: 'var(--muted)' }}>
                                        <span>{(l.received_at || '').slice(0, 19).replace('T', ' ')}</span>
                                        {l.error && <span style={{ color: '#fca5a5' }}>ERRO</span>}
                                    </summary>
                                    <pre className="text-[10px] mt-2 overflow-x-auto" style={{ color: 'var(--text)' }}>{JSON.stringify(l.body || l, null, 2).slice(0, 2000)}</pre>
                                </details>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Pedidos */}
            {subTab === 'orders' && (
                <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="wa-orders">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Pedidos via bot ({orders.length})</h3>
                        <button onClick={loadAll} className="text-xs flex items-center gap-1" style={{ color: 'var(--muted)' }}>
                            <RefreshCw size={11} /> Atualizar
                        </button>
                    </div>
                    {orders.length === 0 ? (
                        <p className="text-xs text-center py-6" style={{ color: 'var(--muted)' }}>Nenhum pedido ainda.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr style={{ color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
                                        <th className="text-left py-2 px-2">Quando</th>
                                        <th className="text-left py-2 px-2">Usuário</th>
                                        <th className="text-left py-2 px-2">Busca</th>
                                        <th className="text-right py-2 px-2">Resultados</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map((o, i) => (
                                        <tr key={i} style={{ borderBottom: '1px solid var(--border)', color: 'var(--text)' }}>
                                            <td className="py-2 px-2">{(o.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                                            <td className="py-2 px-2">{o.profile_name || o.wa_id || '—'}</td>
                                            <td className="py-2 px-2">{o.query || '—'}</td>
                                            <td className="py-2 px-2 text-right">{o.results_count ?? 0}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function HealthRow({ label, ok }) {
    return (
        <div className="flex items-center gap-2 text-xs">
            {ok ? <Check size={14} style={{ color: '#22c55e' }} /> : <X size={14} style={{ color: '#ef4444' }} />}
            <span style={{ color: 'var(--text)' }}>{label}</span>
        </div>
    );
}

function Stat({ label, value, icon: Icon }) {
    return (
        <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: 'var(--muted)' }}>{label}</span>
                <Icon size={14} style={{ color: 'var(--sv-gold-primary)' }} />
            </div>
            <div className="mt-1 text-2xl font-bold" style={{ color: 'var(--text)' }}>{value}</div>
        </div>
    );
}

function Field({ label, value, onChange, placeholder, secret }) {
    return (
        <label className="block">
            <span className="block text-[11px] mb-1" style={{ color: 'var(--muted)' }}>{label}</span>
            <input type={secret ? 'password' : 'text'} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
                className="w-full px-3 py-2 rounded-md text-xs"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
        </label>
    );
}

function KV({ label, value, onCopy, hidden }) {
    if (hidden) return null;
    return (
        <div className="mb-2">
            <p className="text-[11px] mb-1" style={{ color: 'var(--muted)' }}>{label}</p>
            <div className="flex items-stretch gap-1">
                <code className="flex-1 px-2 py-1.5 text-[11px] rounded-md truncate" style={{ background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }}>{value}</code>
                <button onClick={onCopy} className="px-2 rounded-md" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--muted)' }} title="Copiar">
                    <Copy size={12} />
                </button>
            </div>
        </div>
    );
}
